/**
 * Notification panel for the navbar bell icon.
 * Fetches from GET /api/notifications; supports mark read and open from URL (?openNotifications=1).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  Bell,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  CreditCard,
  Megaphone,
  Trash2,
  Settings,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '../ui/Button';
import Badge from '../ui/Badge';
import { notificationService } from '../../services/notificationService';
import type { NotificationHistoryItem } from '../../services/notificationService';

export interface NotificationItemType {
  id: string;
  type: 'payment' | 'reminder' | 'alert' | 'announcement';
  title: string;
  message: string;
  fullMessage?: string;
  time: string;
  read: boolean;
}

function mapBackendTypeToPanelType(backendType: string): NotificationItemType['type'] {
  if (['TENANT_DEPOSIT_PAID', 'TENANT_RENT_PAID', 'TENANT_RENT_PARTIAL'].includes(backendType)) return 'payment';
  if (['TRIAL_ENDING_SOON', 'TRIAL_ENDED', 'BILLING_REMINDER', 'BILLING_DUE', 'RENT_DUE_REMINDER', 'LEASE_SIGNED'].includes(backendType)) return 'reminder';
  if (['TENANT_RENT_OVERDUE', 'PAYMENT_FAILED'].includes(backendType)) return 'alert';
  if (backendType === 'SYSTEM_ANNOUNCEMENT') return 'announcement';
  return 'payment';
}

function formatRelativeTime(createdAt: string): string {
  const d = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString();
}

function mapHistoryToItem(h: NotificationHistoryItem): NotificationItemType {
  const message = h.body.length > 80 ? h.body.slice(0, 80) + '…' : h.body;
  return {
    id: h.id,
    type: mapBackendTypeToPanelType(h.type),
    title: h.title,
    message,
    fullMessage: h.body,
    time: formatRelativeTime(h.createdAt),
    read: h.read,
  };
}

// Styled
const TriggerWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Panel positions relative to this */
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.mutedForeground};
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: ${(p) => p.theme.foreground};
    background: rgba(255, 255, 255, 0.15);
  }
  &:focus-visible {
    outline: none;
  }
  .icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;

const BadgeWrap = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 50%;
  background: ${(p) => p.theme.destructive};
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PanelWrap = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 340px;
  max-width: calc(100vw - 24px);
  max-height: 420px;
  background: ${(p) => p.theme.card};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 12px;
  box-shadow: ${(p) => p.theme.shadowLg || '0 10px 40px rgba(0,0,0,0.12)'};
  z-index: 1001;
  display: flex;
  flex-direction: column;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transform: ${(p) => (p.$open ? 'translateY(0)' : 'translateY(-8px)')};
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;

  @media (max-width: 640px) {
    position: fixed;
    top: 72px;
    left: 12px;
    right: 12px;
    width: auto;
    max-width: none;
    max-height: calc(100vh - 88px);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.foreground};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PanelTabsList = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 12px 0;
  border-bottom: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const PanelTab = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  background: ${(p) => (p.$active ? `${p.theme.primary}20` : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.mutedForeground)};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(p) => p.theme.muted};
    color: ${(p) => p.theme.foreground};
  }
`;

const ScrollList = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  max-height: 280px;
`;

const NotificationRow = styled.div<{ $unread: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: ${(p) => (p.$unread ? `${p.theme.primary}08` : 'transparent')};
  position: relative;

  &:hover {
    background: ${(p) => p.theme.muted};
  }
  &:last-child {
    border-bottom: none;
  }
`;

const UnreadDot = styled.span`
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.theme.primary};
`;

const IconCircle = styled.div<{ $variant: string }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => {
    const v = p.$variant;
    if (v === 'payment') return `background: rgba(16, 185, 129, 0.2); color: #10b981;`;
    if (v === 'reminder') return `background: rgba(59, 130, 246, 0.2); color: #3b82f6;`;
    if (v === 'alert') return `background: rgba(239, 68, 68, 0.2); color: #ef4444;`;
    return `background: ${p.theme.primary}20; color: ${p.theme.primary};`;
  }}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const NotificationBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.p<{ $unread: boolean }>`
  font-size: 13px;
  font-weight: ${(p) => (p.$unread ? 600 : 500)};
  color: ${(p) => p.theme.foreground};
  margin: 0 0 2px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const NotificationTime = styled.span`
  font-size: 10px;
  color: ${(p) => p.theme.mutedForeground};
  white-space: nowrap;
`;

const NotificationMessage = styled.p`
  font-size: 12px;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RowActions = styled.div<{ $show: boolean }>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: ${(p) => (p.$show ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const DetailContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => p.theme.foreground};
  white-space: pre-line;
`;

const DetailFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(p) => p.theme.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: ${(p) => p.theme.mutedForeground};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const EmptyTitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => p.theme.foreground};
  margin: 0 0 4px 0;
`;

const EmptySub = styled.p`
  font-size: 12px;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0;
`;

// Helpers
function getNotificationIcon(type: NotificationItemType['type']) {
  switch (type) {
    case 'payment':
      return <CreditCard />;
    case 'reminder':
      return <Clock />;
    case 'alert':
      return <AlertCircle />;
    case 'announcement':
      return <Megaphone />;
  }
}

function EmptyState({ message = "You're all caught up!" }: { message?: string }) {
  return (
    <EmptyWrap>
      <EmptyIcon>
        <Bell />
      </EmptyIcon>
      <EmptyTitle>{message}</EmptyTitle>
      <EmptySub>We'll notify you when something arrives</EmptySub>
    </EmptyWrap>
  );
}

interface NotificationRowItemProps {
  notification: NotificationItemType;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
  onOpen: (n: NotificationItemType) => void;
}

function NotificationRowItem({
  notification,
  onMarkRead,
  onDelete,
  onOpen,
}: NotificationRowItemProps) {
  const [hover, setHover] = useState(false);

  return (
    <NotificationRow
      $unread={!notification.read}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onOpen(notification)}
    >
      {!notification.read && <UnreadDot />}
      <IconCircle $variant={notification.type}>{getNotificationIcon(notification.type)}</IconCircle>
      <NotificationBody>
        <NotificationTitle $unread={!notification.read}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {notification.title}
          </span>
          <NotificationTime>{notification.time}</NotificationTime>
        </NotificationTitle>
        <NotificationMessage>{notification.message}</NotificationMessage>
      </NotificationBody>
      <RowActions $show={hover}>
        {!notification.read && (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onMarkRead(notification.id);
            }}
            style={{ minWidth: 28, height: 28, padding: 0 }}
          >
            <Check size={14} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          style={{ minWidth: 28, height: 28, padding: 0, color: 'var(--destructive)' }}
        >
          <Trash2 size={14} />
        </Button>
      </RowActions>
    </NotificationRow>
  );
}

export function NotificationPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState<NotificationItemType[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<NotificationItemType | null>(null);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [serverUnreadCount, setServerUnreadCount] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationService.list({ limit: 30 });
      setNotifications(res.items.map(mapHistoryToItem));
      // Also fetch unread count from server to ensure accuracy
      try {
        const count = await notificationService.getUnreadCount();
        setServerUnreadCount(count);
      } catch {
        // Silently fail - use local calculation
      }
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Open panel when landing from notification click (e.g. /?openNotifications=1)
  useEffect(() => {
    const shouldOpen = searchParams.get('openNotifications') === '1';
    if (shouldOpen) {
      setOpen(true);
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('openNotifications');
        return p;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch only when panel is opened (not on mount) to avoid extra API load on property/page load
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Use server count if available, otherwise calculate from local state
  const unreadCount = serverUnreadCount !== null ? serverUnreadCount : notifications.filter((n) => !n.read).length;

  // Poll for counter updates when panel is closed (every 2 minutes to avoid excessive /api/notifications/unread-count calls)
  useEffect(() => {
    if (open) return;
    const interval = setInterval(async () => {
      try {
        const count = await notificationService.getUnreadCount();
        setServerUnreadCount(count);
      } catch {
        // Silently fail
      }
    }, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [open]);

  const queryClient = useQueryClient();

  // Listen for service worker messages (push notifications)
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NOTIFICATION_RECEIVED') {
        // Refresh notifications and counter when push notification arrives
        try {
          const count = await notificationService.getUnreadCount();
          setServerUnreadCount(count);
        } catch {
          // Silently fail
        }
        if (open) {
          fetchNotifications();
        }
        // Invalidate payments so landlord payments table updates when a payment push is received
        queryClient.invalidateQueries({ queryKey: ['payments'] });
        queryClient.invalidateQueries({ queryKey: ['paymentAnalytics'] });
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [open, fetchNotifications, queryClient]);
  const unreadList = notifications.filter((n) => !n.read);
  const paymentList = notifications.filter((n) => n.type === 'payment');
  const alertList = notifications.filter((n) => n.type === 'alert');

  const getFilteredList = () => {
    if (tab === 'unread') return unreadList;
    if (tab === 'payments') return paymentList;
    if (tab === 'alerts') return alertList;
    return notifications;
  };

  const handleMarkRead = async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await notificationService.markRead(id);
      // Refresh unread count from server to ensure accuracy
      try {
        const count = await notificationService.getUnreadCount();
        setServerUnreadCount(count);
        // Update local state if needed (in case there are notifications not in local list)
        const localUnread = notifications.filter((n) => !n.read && n.id !== id).length;
        if (count !== localUnread) {
          fetchNotifications();
        }
      } catch {
        // Silently fail - local state is already updated
      }
    } catch {
      // Revert on error
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)));
    }
  };

  const handleDelete = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setServerUnreadCount(0); // Optimistically set to 0
    try {
      await notificationService.markAllRead();
      // Refresh from server to ensure all notifications are marked as read (including those not in local list)
      await fetchNotifications();
      // Also verify unread count is 0
      try {
        const count = await notificationService.getUnreadCount();
        setServerUnreadCount(count);
        if (count > 0) {
          // If count is still > 0, refresh again to get all notifications
          await fetchNotifications();
        }
      } catch {
        // Silently fail - already set to 0 optimistically
      }
    } catch {
      // On error, refresh to get accurate state
      fetchNotifications();
    }
  };

  const handleClearAll = async () => {
    await handleMarkAllRead();
    setSelected(null);
  };

  const handleOpen = async (n: NotificationItemType) => {
    if (!n.read) {
      await handleMarkRead(n.id);
      // Update selected notification with read status after marking as read
      setSelected({ ...n, read: true });
    } else {
      setSelected(n);
    }
  };

  const handleCloseDetail = () => setSelected(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        open &&
        panelRef.current &&
        !panelRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
        setSelected(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filteredList = getFilteredList();

  return (
    <TriggerWrap ref={triggerRef} onClick={(e) => e.stopPropagation()}>
      <TriggerButton
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <div className="icon-wrapper">
          <Bell size={20} />
          {unreadCount > 0 && (
            <BadgeWrap>{unreadCount > 9 ? '9+' : unreadCount}</BadgeWrap>
          )}
        </div>
      </TriggerButton>

      <PanelWrap ref={panelRef} $open={open}>
        {selected ? (
          <>
            <DetailHeader>
              <Button variant="ghost" size="sm" type="button" onClick={handleCloseDetail} style={{ minWidth: 32, height: 32, padding: 0 }}>
                <ArrowLeft size={16} />
              </Button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selected.title}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--muted-foreground)' }}>{selected.time}</p>
              </div>
              <IconCircle $variant={selected.type}>{getNotificationIcon(selected.type)}</IconCircle>
            </DetailHeader>
            <DetailContent>{selected.fullMessage || selected.message}</DetailContent>
            <DetailFooter>
              <Button variant="outline" size="sm" onClick={() => handleDelete(selected.id)}>
                <Trash2 size={14} style={{ marginRight: 6 }} />
                Delete
              </Button>
              <Button variant="primary" size="sm">
                Take Action
              </Button>
            </DetailFooter>
          </>
        ) : (
          <>
            <PanelHeader>
              <PanelTitle>
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="secondary" size="sm">
                    {unreadCount} new
                  </Badge>
                )}
              </PanelTitle>
              <PanelHeaderActions>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead} style={{ fontSize: 12 }}>
                    <CheckCheck size={12} style={{ marginRight: 4 }} />
                    Mark all
                  </Button>
                )}
                <Button variant="ghost" size="sm" style={{ minWidth: 28, height: 28, padding: 0 }}>
                  <Settings size={14} />
                </Button>
              </PanelHeaderActions>
            </PanelHeader>

            <PanelTabsList>
              {[
                { value: 'all', label: 'All' },
                { value: 'unread', label: `Unread (${unreadCount})` },
                { value: 'payments', label: 'Payments' },
                { value: 'alerts', label: 'Alerts' },
              ].map((t) => (
                <PanelTab key={t.value} $active={tab === t.value} onClick={() => setTab(t.value)}>
                  {t.label}
                </PanelTab>
              ))}
            </PanelTabsList>

            <ScrollList>
              {loading && notifications.length === 0 ? (
                <EmptyState message="Loading…" />
              ) : filteredList.length > 0 ? (
                filteredList.map((n) => (
                  <NotificationRowItem
                    key={n.id}
                    notification={n}
                    onMarkRead={handleMarkRead}
                    onDelete={handleDelete}
                    onOpen={handleOpen}
                  />
                ))
              ) : (
                <EmptyState
                  message={
                    tab === 'unread'
                      ? 'No unread notifications'
                      : tab === 'payments'
                        ? 'No payment notifications'
                        : tab === 'alerts'
                          ? 'No alerts'
                          : "You're all caught up!"
                  }
                />
              )}
            </ScrollList>

            {notifications.length > 0 && (
              <PanelFooter>
                <Button variant="ghost" size="sm" onClick={handleClearAll} style={{ fontSize: 12, color: 'var(--destructive)' }}>
                  Clear all
                </Button>
                <Button variant="ghost" size="sm" style={{ fontSize: 12 }}>
                  View all notifications
                </Button>
              </PanelFooter>
            )}
          </>
        )}
      </PanelWrap>
    </TriggerWrap>
  );
}

export default NotificationPanel;
